import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { TagChip } from '../../../components/entity-detail/TagChip.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { ContactCompanyField } from './ContactCompanyField.js';
import {
  formatDate,
  formatDateTime,
  formatRelative,
  formatRelativeShort,
} from '../../../shared/format/entity-detail.js';
import type { ContactItem, ContactInteractionItem } from '../api/contacts.client.js';

export function ContactSidebar({
  contact,
  interactions,
}: {
  contact: ContactItem;
  interactions: ContactInteractionItem[];
}): ReactElement {
  const { t } = useTranslation();
  const active = contact.deals.filter((deal) => deal.stage !== 'Won' && deal.stage !== 'Lost');
  const last = [...interactions].sort(
    (a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt),
  )[0];
  return (
    <>
      <section className="entity-panel">
        <EntityMetric
          testId="contact-summary-deals"
          label={t('crm.entity_detail.active_deals')}
          value={active.length}
        />
        {active.map((deal) => (
          <div key={deal.id} style={{ marginTop: 8 }}>
            <Link to={`/deals/${deal.id}`}>{deal.title}</Link>
          </div>
        ))}
      </section>
      <section className="entity-panel">
        <EntityMetric
          testId="contact-last-activity"
          label={t('crm.entity_detail.last_activity')}
          value={last ? formatRelativeShort(last.occurredAt) : t('crm.entity_detail.empty_history')}
          exact={
            last
              ? `${formatRelative(last.occurredAt)} · ${formatDateTime(last.occurredAt)}`
              : undefined
          }
          hint={
            last
              ? `${t(`crm.entity_detail.interaction_${last.type}`)} · ${formatDate(last.occurredAt)}`
              : undefined
          }
        />
      </section>
      <section className="entity-panel">
        <h2>{t('crm.entity_detail.details')}</h2>
        <EntityFieldList
          fields={[
            {
              key: 'phone',
              label: t('crm.contacts.form.phone'),
              value: contact.phone ? (
                <a href={`tel:${contact.phone}`} style={{ fontFamily: 'inherit' }}>
                  {contact.phone}
                </a>
              ) : null,
              copy: contact.phone ?? undefined,
            },
            {
              key: 'email',
              label: t('crm.contacts.form.email'),
              value: contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : null,
              copy: contact.email ?? undefined,
            },
            {
              key: 'company',
              label: t('crm.features.features.contacts.pages.contactdetailpage.компания'),
              value: <ContactCompanyField contact={contact} />,
            },
            ...(contact.role
              ? [{ key: 'role', label: t('crm.entity_detail.role'), value: contact.role }]
              : []),
            {
              key: 'source',
              label: t('crm.features.features.contacts.pages.contactdetailpage.источник'),
              value: (
                <span data-testid="contact-summary-source">
                  {contact.source ? getEnumLabel('contactSource', contact.source, t) : '—'}
                </span>
              ),
            },
            {
              key: 'tags',
              label: t('crm.entity_detail.tags'),
              value: contact.tags.length ? (
                <div className="flex flex-wrap gap-1">
                  {contact.tags.map((tag) => (
                    <TagChip key={tag} value={tag} />
                  ))}
                </div>
              ) : (
                '—'
              ),
            },
            {
              key: 'created',
              label: t('crm.entity_detail.created'),
              value: formatDateTime(contact.createdAt),
            },
            {
              key: 'updated',
              label: t('crm.entity_detail.updated'),
              value: formatDateTime(contact.updatedAt),
            },
          ]}
        />
      </section>
    </>
  );
}
